import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowRight, Shield, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import PiLoginButton from '../components/atoms/PiLoginButton.jsx';
import GoogleLoginButton from '../components/atoms/GoogleLoginButton.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import Logo from '../components/ui/Logo';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, user, refreshUserData, isPiBrowser, isPiBrowserDetecting } = useAuth();
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, from]);

  const handleLoginSuccess = async (result) => {
    try {
      // Refresh AuthContext state from backend after successful Google auth
      await refreshUserData();
      
      // Navigate to dashboard or profile completion page based on profile status
      const destination = result.data?.profileStatus?.isComplete ? '/dashboard' : '/dashboard';
      navigate(destination, { replace: true });
    } catch (error) {
      console.error('❌ Failed to refresh auth state:', error);
      // Still try to navigate even if refresh fails
      navigate('/dashboard', { replace: true });
    }
  };

  const handleLoginError = (error) => {
    console.error('❌ Login error:', error);
    
    // You can add a toast notification here
    // toast.error(error);
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4"
          />
          <p className="text-white text-l2-r">Loading...</p>
        </motion.div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.8
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen gradient-hero relative overflow-hidden">
      {/* Simplified Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0 opacity-[0.05]"
          animate={{ backgroundPosition: ['0px 0px', '60px 60px'] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          style={{
            backgroundImage: `radial-gradient(circle at center, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="relative flex items-center justify-center min-h-screen px-4 py-6 sm:py-8 login-mobile-container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-sm w-full space-y-6 sm:space-y-8"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center space-y-4 sm:space-y-6">
            {/* Logo */}
            <div className="flex justify-center">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <div className="h-16 w-16 sm:h-20 sm:w-20 bg-white/15 backdrop-blur-lg rounded-2xl flex items-center justify-center shadow-xl border border-white/20">
                  <Logo
                    variant="mark"
                    size="custom"
                    theme="light"
                    className="h-10 sm:h-12 w-auto"
                    alt="Crowblock Logo"
                  />
                </div>
              </motion.div>
            </div>

            <div className="space-y-1 sm:space-y-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading text-white font-semibold leading-tight enhanced-hierarchy">
                Welcome Back
              </h1>
              <p className="text-white/80 text-sm sm:text-base font-light">
                 Sign in to continue to Crowblock
              </p>
            </div>
          </motion.div>

          {/* Login Card */}
          <motion.div variants={itemVariants}>
            <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-xl login-mobile-card">
              <CardContent className="p-6 sm:p-8 space-y-4 sm:space-y-6">
                {/* Pi Login Button - Always show in Pi Browser, optional in regular browser */}
                {(isPiBrowser === true || isPiBrowserDetecting) && (
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <PiLoginButton
                      size="lg"
                      className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-medium mobile-touch-target focus-enhanced premium-glow"
                      onSuccess={handleLoginSuccess}
                      onError={handleLoginError}
                    />
                  </motion.div>
                )}

                {/* Divider - Only show when both login options are available */}
                {isPiBrowser === false && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                      </div>
                    </div>

                    {/* Google Login Button - Only show in regular browsers */}
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <GoogleLoginButton
                        size="lg"
                        className="w-full"
                        onSuccess={handleLoginSuccess}
                        onError={handleLoginError}
                      />
                    </motion.div>
                  </>
                )}

                {/* Terms */}
                <div className="text-center text-xs text-white/60">
                  <p>
                    By signing in, you agree to our{' '}
                    <a href="/terms" className="text-white/80 hover:text-white underline focus-enhanced">
                      Terms
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-white/80 hover:text-white underline focus-enhanced">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer */}
          <motion.div variants={itemVariants} className="text-center space-y-2">
            {/* Pi Browser specific message */}
            {isPiBrowser === true && (
              <p className="text-xs sm:text-sm text-white/80 font-medium">
                🌐 Running in Pi Browser
              </p>
            )}
            
            {/* Regular browser message */}
            {isPiBrowser === false && (
              <p className="text-xs sm:text-sm text-white/60">
                💡 Use Pi Browser for full Pi Network features
              </p>
            )}
            
            <p className="text-xs sm:text-sm text-white/60">
              New to Pi Network?{' '}
              <a
                href="https://minepi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white underline font-medium focus-enhanced"
              >
                Get started here
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;